import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useEffect, useState, type ChangeEvent, type FormEvent, type KeyboardEvent } from 'react';
import { Edit3 } from 'react-feather';
import type { ShiritoriGameStore } from '@/game/core/game.store';
import { GameImage } from './game-image';
import type { ShiritoriWordDto } from '@/game/core/shiritori-word';
import { Card } from '../home/card';
import { Input } from '../home/input';
import { Button, ImageChoiceButton } from '../home/buttons';
import { useTranslation } from 'react-i18next';

interface WordSearchPanelProps {
    game: ShiritoriGameStore;
}

export const WordSearchPanel = observer(function ({ game }: WordSearchPanelProps) {
    const { t } = useTranslation();
    const [romajiInput, setRomajiInput] = useState('');
    const [homonymResult, setHomonymResult] = useState<ShiritoriWordDto[]>([]);
    const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(game.currentTurnRemainingSeconds());

    const isTimerEnabled = game.isTimerActive;
    const timerKey = game.turnTimerKey;
    const isTimedOut = remainingSeconds !== undefined && remainingSeconds <= 0;

    useEffect(() => {
        if (!isTimerEnabled) {
            setRemainingSeconds(undefined);
            return;
        }

        game.markTurnSeen();
    }, [game, isTimerEnabled, game.currentTurnIndex]);

    useEffect(() => {
        if (!isTimerEnabled) {
            setRemainingSeconds(undefined);
            return;
        }

        const tick = () => {
            const remaining = game.currentTurnRemainingSeconds();
            setRemainingSeconds(remaining);

            if (remaining !== undefined && remaining <= 0) {
                game.expireTurnIfNeeded();
            }
        };

        tick();
        const id = setInterval(tick, 1000);

        return () => clearInterval(id);
    }, [game, timerKey, isTimerEnabled, game.currentTurnIndex]);

    function reset() {
        setRomajiInput('');
        setHomonymResult([]);
    }

    async function findWords(value: string) {
        if (isTimedOut) {
            return;
        }

        let result: ShiritoriWordDto[] = [];
        if (!value.trim()) {
            result = [];
        } else {
            if (game.isKanjiInput(value)) {
                const kanjiMatches = await game.searchKanji(value);

                if (!kanjiMatches.length) {
                    toast.error(t('game.gameplay.wordSearch.toastNoSuchWord'), {
                        position: 'top-center',
                        duration: 1250,
                    });
                    reset();
                    return;
                }

                const playableMatches = kanjiMatches.filter((word) => game.wordIsPlayable(word.kana).ok);
                if (playableMatches.length) {
                    if (playableMatches.length === 1) {
                        selectWord(playableMatches[0]!);
                    } else {
                        setHomonymResult(playableMatches);
                    }
                    return;
                }

                const { messageKey } = game.wordIsPlayable(kanjiMatches[0]!.kana);
                const validationMessage = messageKey
                    ? t(`game.gameplay.wordSearch.validation.${messageKey}`)
                    : t('game.gameplay.wordSearch.toastCantPlay');

                toast.error(validationMessage, {
                    position: 'top-center',
                    duration: 1250,
                });
                return;
            }

            const kana = game.nomalizeToKana(value);
            const { ok, messageKey } = game.wordIsPlayable(kana);
            if (ok) {
                result = await game.searchWord(kana);
                if (!result || !result.length) {
                    toast.error(t('game.gameplay.wordSearch.toastNoSuchWord'), {
                        position: 'top-center',
                        duration: 1250,
                    });
                    reset();
                } else {
                    if (result.length === 1) {
                        selectWord(result[0]!);
                    } else {
                        setHomonymResult(result);
                    }
                }
            } else {
                const validationMessage = messageKey
                    ? t(`game.gameplay.wordSearch.validation.${messageKey}`)
                    : t('game.gameplay.wordSearch.toastCantPlay');

                toast.error(validationMessage, {
                    position: 'top-center',
                    duration: 1250,
                });
            }
        }
    }

    function onSubmit(event: FormEvent) {
        event.preventDefault();
        findWords(romajiInput);
    }

    function onKeyPress(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === 'Escape') {
            event.preventDefault();
            reset();
        }
    }

    function onInputChange(event: ChangeEvent<HTMLInputElement>) {
        setRomajiInput(event.target.value);
    }

    async function selectWord(word: ShiritoriWordDto) {
        console.log('selected word', word);
        try {
            await game.playWord(word);
            toast.success(t('game.gameplay.wordSearch.toastPlayed', { word: word.kana }), {
                position: 'top-center',
                duration: 2500,
            });
            reset();
        } catch (error) {
            toast.error(t('game.gameplay.wordSearch.toastPlayFailed'), {
                position: 'top-center',
                duration: 1250,
            });
        }
    }

    return (
        <div className="fixed left-0 bottom-0 w-screen flex justify-center mb-10">
            <Card className="h-full w-lg max-w-2xl shadow-lg p-4 md:p-6 flex flex-col gap-4">
                {homonymResult.length > 0 && (
                    <>
                        <p className="text-xs font-semibold text-center">{t('game.gameplay.wordSearch.multipleMatches')}</p>
                        <div className={`grid grid-cols-5 gap-2 rounded-lg`}>
                            {homonymResult.map((word, index) => (
                                <ImageChoiceButton hotkey={{ key: String(index + 1), display: index + 1, allowInInput: true }} key={word.imageId} onClick={() => selectWord(word)}>
                                    <GameImage game={game} match={word} />
                                </ImageChoiceButton>
                            ))}
                        </div>
                    </>
                )}

                <div className="flex items-center justify-between gap-3">
                    <p className="text-xs tracking-wide opacity-60">{t('game.gameplay.wordSearch.yourTurn')}</p>
                    <div className="flex items-center gap-2">
                        {remainingSeconds !== undefined && (
                            <span className="text-xs font-semibold text-game-highlight px-2 py-1 rounded-full">
                                {t('game.gameplay.wordSearch.timerSeconds', { seconds: remainingSeconds })}
                            </span>
                        )}
                        <Edit3 className="w-5 h-5 opacity-60" />
                    </div>
                </div>

                <form onSubmit={onSubmit} autoComplete="off" spellCheck="false" className="space-y-2">
                    <Input
                        inputMode="kana"
                        autoFocus
                        id="romaji-input"
                        disabled={isTimedOut}
                        placeholder={t('game.gameplay.wordSearch.placeholder')}
                        value={romajiInput}
                        onKeyDown={onKeyPress}
                        onChange={onInputChange}
                    />
                    <Button hotkey={{ key: 'Enter', display: '⏎' }} type="submit" disabled={!romajiInput.trim() || isTimedOut} className="w-full">
                        {t('game.gameplay.wordSearch.playAction')}
                    </Button>
                </form>
            </Card>
        </div>
    );
});
